import React, { useMemo } from 'react'
import { Calendar, Pencil, Bot, RotateCcw, Save, Loader2, ChevronDown } from 'lucide-react'
import { getDocumentTypeLabel, getDocumentTypeInfo, DOCUMENT_TYPES } from '../../constants/documentTypes'
import { useClassificationEditor } from './hooks/useClassificationEditor'
import type { ClassificationPanelProps } from './types'
import styles from '../../pages/DocumentsPage.module.css'

export const ClassificationPanel: React.FC<ClassificationPanelProps> = ({
  classification,
  docId,
  caseId,
  stage,
  onApprove,
  onContinue,
  isApproving,
  classificationsCount,
}) => {
  // Classification editor for the current document
  const classificationEditor = useClassificationEditor(
    classification,
    docId,
    caseId,
    classification?.filename
  )

  const {
    selectedType,
    selectedYear,
    isEditing,
    hasChanges,
    handleEdit,
    handleReset,
    handleSave,
    setSelectedType,
    setSelectedYear,
    setIsEditing,
    isSaving,
  } = classificationEditor

  // Year options for dropdown
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years: string[] = []
    for (let y = currentYear; y >= 2018; y--) {
      years.push(y.toString())
    }
    return years
  }, [])

  const typeInfo = classification ? getDocumentTypeInfo(classification.document_type) : null
  const isLowConfidence = classification && classification.confidence < 0.8

  const onSaveClick = async () => {
    await handleSave()
    setIsEditing(false)
  }

  return (
    <div className={styles.detailsPane}>
      {/* AI Classification Status */}
      {classification && (
        <div className={styles.aiStatusSection}>
          <div className={styles.aiStatusHeader}>
            <div className={styles.aiStatusBadge}>
              <Bot size={14} />
              <span>Classified by Fenero AI</span>
            </div>
            <span className={styles.confidenceChip}>
              {Math.round(classification.confidence * 100)}% confident
            </span>
          </div>
          {hasChanges && (
            <div className={styles.editedBadge}>
              <Pencil size={12} />
              <span>Modified from AI result</span>
            </div>
          )}
        </div>
      )}

      <div className={styles.detailsSection}>
        <div className={styles.sectionHeader}>
          <h4>Classification</h4>
          {classification && !isEditing && (
            <button
              className={styles.editBtn}
              onClick={handleEdit}
            >
              <Pencil size={12} />
              Edit
            </button>
          )}
          {isEditing && hasChanges && (
            <button
              className={styles.resetBtn}
              onClick={handleReset}
            >
              <RotateCcw size={12} />
              Reset
            </button>
          )}
        </div>

        {/* Document Type */}
        <div className={styles.fieldGroup}>
          <label>Document Type</label>
          {isEditing ? (
            <div className={styles.selectWrapper}>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">Select type...</option>
                {DOCUMENT_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className={styles.selectIcon} />
            </div>
          ) : (
            <div className={styles.readOnlyValue}>
              {selectedType ? getDocumentTypeLabel(selectedType) : 'Not classified'}
            </div>
          )}
        </div>

        {/* Tax Year (if applicable) */}
        {(typeInfo?.category === 'tax' || classification?.tax_year || selectedType.includes('1040') || selectedType.includes('w2')) && (
          <div className={styles.fieldGroup}>
            <label><Calendar size={12} /> Tax Year</label>
            {isEditing ? (
              <div className={styles.selectWrapper}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <option value="">Select year...</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <ChevronDown size={14} className={styles.selectIcon} />
              </div>
            ) : (
              <div className={styles.readOnlyValue}>
                {selectedYear || 'Not set'}
              </div>
            )}
          </div>
        )}

        {/* Edit Mode Actions */}
        {isEditing && (
          <div className={styles.editActions}>
            <button
              className={styles.cancelEditBtn}
              onClick={handleReset}
            >
              Cancel
            </button>
            <button
              className={styles.saveBtn}
              onClick={onSaveClick}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <><Loader2 size={14} className={styles.spinner} /> Saving...</>
              ) : (
                <><Save size={14} /> Save Changes</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Approval Action - show when in review stage */}
      {stage === 'review' && onApprove && (
        <div className={styles.approvalSection}>
          <div className={styles.approvalInfo}>
            <span>{classificationsCount || 0} documents classified</span>
          </div>
          <button
            className={styles.approveBtn}
            onClick={onApprove}
            disabled={isApproving}
          >
            {isApproving ? (
              <><Loader2 size={16} className={styles.spinner} /> Approving...</>
            ) : (
              <><span>Approve All Classifications</span></>
            )}
          </button>
        </div>
      )}

      {/* Already Approved - show continue option */}
      {(stage === 'approved' || stage === 'approved_with_new') && onContinue && (
        <div className={styles.approvalSection}>
          <div className={styles.approvedInfo}>
            <span>Classifications approved</span>
          </div>
          <button className={styles.continueBtn} onClick={onContinue}>
            Continue to Income
          </button>
        </div>
      )}

      {/* Key Information */}
      {classification?.key_information && Object.keys(classification.key_information).length > 0 && (
        <div className={styles.detailsSection}>
          <h4>Extracted Data</h4>
          <div className={styles.extractedData}>
            {Object.entries(classification.key_information).map(([key, value]) => (
              <div key={key} className={styles.dataRow}>
                <span className={styles.dataLabel}>{key.replace(/_/g, ' ')}</span>
                <span className={styles.dataValue}>
                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {classification?.summary && (
        <div className={styles.detailsSection}>
          <h4>Summary</h4>
          <p className={styles.summaryText}>{classification.summary}</p>
        </div>
      )}
    </div>
  )
}

